import { memo, useState } from 'react';
import { getEntityAvatarUrl, getAvatarSource, AVATAR_GRADIENTS, AVATAR_COLORS } from '@/lib/avatars';

/**
 * Reusable entity avatar.
 *
 * Two equivalent API shapes:
 *
 *   <EntityAvatar entity={entityObject} size={48} />        ← preferred
 *   <EntityAvatar name="…" entityType="…" sex="…" thumbnail="…" size={48} />
 *
 * Passing the full entity is cleaner and lets the component pick the best
 * source (real Wikipedia photo > gender-aware DiceBear > initials).
 *
 * Performance: for any list-view size (≤ 96 px rendered) we swap the
 * original-quality PNG/JPG (600-2200 KB) for a pre-generated 192×192
 * WebP thumbnail (~15-25 KB) that lives at `/api/static/thumbs/<id>.webp`.
 * On the EntityDetail hero card we keep the original. Generated by
 * `scripts/generate_thumbnails.py`. If the thumb 404s the <img>
 * `onError` falls back to the original URL automatically.
 */
function deriveThumbUrl(url) {
  if (!url || typeof url !== 'string') return null;
  // Pattern: /api/static/{generated_avatars|wikipedia_cache}/<basename>.<ext>
  const m = url.match(/(\/api\/static\/)(?:generated_avatars|wikipedia_cache)\/([^/?#]+?)\.(?:png|jpg|jpeg|webp)(?:[?#]|$)/i);
  if (!m) return null;
  // 2026-07 — carry the `?v=<epoch>` cache-buster over to the thumb URL.
  // Dropping it made every list view hit the un-versioned webp, which
  // Cloudflare/browsers cache for 30 days → regenerated portraits kept
  // showing the OLD thumb even though the full-res URL was busted.
  const q = url.match(/\?[^#]*/);
  return `${m[1]}thumbs/${m[2]}.webp${q ? q[0] : ''}`;
}

export default memo(function EntityAvatar({
  entity,
  name,
  entityType,
  sex,
  thumbnail,
  size = 40,
  className = '',
}) {
  const [imgError, setImgError] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  // Normalise the two API shapes into a single `entity-like` object.
  const e = entity || {
    name,
    entity_type: entityType,
    metadata: { sex, thumbnail },
  };

  const { src: avatarUrl, sourceKind } = getAvatarSource(e, size * 2);
  // DiceBear path: keep the legacy fallback hook so we can swap to initials.
  const dicebearFallback = getEntityAvatarUrl(
    e?.name,
    e?.entity_type,
    size * 2,
    e?.metadata?.sex,
  );

  // Pick the cheap thumb for list-view sizes; keep original for hero card.
  const wantsThumb = size <= 96 && sourceKind === 'real';
  const thumbUrl = wantsThumb ? deriveThumbUrl(avatarUrl) : null;
  const finalSrc = imgError && sourceKind === 'real'
    ? dicebearFallback
    : (thumbUrl && !thumbError ? thumbUrl : avatarUrl);

  const bgGradient = AVATAR_GRADIENTS[e?.entity_type] || AVATAR_GRADIENTS.athlete;
  const bgColor = AVATAR_COLORS[e?.entity_type] || '#666';
  const initial = e?.name?.charAt(0) || '?';

  // RIP rendering — deceased people are shown with a desaturated, slightly
  // dimmed avatar so they're instantly recognisable across every surface
  // (Stage roster, EntityDetail, search-result row in CMS, etc.). The
  // single source of truth is `metadata.death_date` — admin takedowns
  // and opt-outs use `active=false` but are NOT memorial deaths.
  const isDeceased = Boolean(e?.metadata?.death_date);

  const sizeClass =
    size <= 32 ? 'h-8 w-8 text-xs' :
    size <= 48 ? 'h-10 w-10 text-sm' :
    size <= 64 ? 'h-14 w-14 text-lg' :
    'h-16 w-16 text-xl';

  return (
    <div
      className={`rounded-full flex items-center justify-center overflow-hidden shrink-0 ${sizeClass} ${className}`}
      style={{
        background: bgGradient,
        filter: isDeceased ? 'grayscale(1) brightness(0.85)' : undefined,
      }}
      data-source-kind={sourceKind}
      data-deceased={isDeceased ? 'true' : undefined}
      title={isDeceased ? `Deceased ${e?.metadata?.death_date || ''}`.trim() : undefined}
    >
      {!imgError || sourceKind === 'real' ? (
        <img
          src={finalSrc}
          alt={e?.name || 'Entity'}
          className="h-full w-full object-cover"
          onError={() => {
            // First failure: thumb missing → retry with the original.
            // Second failure: original also dead → cascade to DiceBear /
            // initials by flipping `imgError`.
            if (thumbUrl && !thumbError && finalSrc === thumbUrl) {
              setThumbError(true);
            } else {
              setImgError(true);
            }
          }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="text-white font-bold" style={{ background: bgColor }}>{initial}</span>
      )}
    </div>
  );
});
// PERF (v5.7.37): memoized so the 18 sprites in the Stage arena don't
// re-render every time `floaters`/`liveBumps` update in the parent. The
// entity object reference IS stable across renders (it sits in `team.slots`
// which only mutates on draft/transfer/remove), so React.memo's default
// shallow comparison is sufficient.
